import axios from "axios";
import { useAtom, useSetAtom } from "jotai";
import { environment } from "../../environment";
import { errorCatcher, useSnackbar } from "../../hooks";
import { useLogin } from "../login";
import { ITrackableItemZoneAllowlist, ITrackableItemZoneAllowlistInput, allowlistAtom, allowlistLoadingAtom } from "./types";

type UseAllowlist = [ITrackableItemZoneAllowlist[], {
    list: (query?: Record<string, any>) => Promise<ITrackableItemZoneAllowlist[] | null>;
    getById: (id: number) => Promise<ITrackableItemZoneAllowlist | null>;
    getPaginated: (first?: number, offset?: number, query?: Record<string, any>) => Promise<ITrackableItemZoneAllowlist[] | null>;
    create: (input: ITrackableItemZoneAllowlistInput) => Promise<ITrackableItemZoneAllowlist | null>;
    update: (id: number, input: ITrackableItemZoneAllowlistInput) => Promise<ITrackableItemZoneAllowlist | null>;
    remove: (id: number) => Promise<ITrackableItemZoneAllowlist | null>;
}];

const gqlEndpoint = `${environment.baseUrl}/graphql`;
const graphqlRequest = async (authToken: string | null, query: string, variables?: any) => {
    const { data } = await axios.post(gqlEndpoint, { query, variables }, { headers: { 'X-Wallmates-Auth': authToken } });
    if (data.errors) throw new Error(data.errors[0]?.message || 'GraphQL error');
    return data.data;
}

export const useTrackableItemZoneAllowlist = (): UseAllowlist => {
    const [items, setItems] = useAtom(allowlistAtom);
    const setLoading = useSetAtom(allowlistLoadingAtom);
    const [, setSnackbar] = useSnackbar();
    const [, authToken, loginFunctions] = useLogin();

    const list = async (query: Record<string, any> = {}) => {
        try { setLoading(true);
            const q = `query($query: JSON){ getTrackableItemZoneAllowlists(query: $query){ id trackable_item_id zone_id } }`;
            const data = await graphqlRequest(authToken, q, { query });
            setItems(data.getTrackableItemZoneAllowlists);
            return data.getTrackableItemZoneAllowlists as ITrackableItemZoneAllowlist[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); setSnackbar({show:true,snackbarLevel:'error',text:'Failed to fetch allowlist.'}); return null; }
        finally { setLoading(false);} };

    const getById = async (id: number) => {
        try { const q = `query($id: ID!){ getTrackableItemZoneAllowlistById(id:$id){ id trackable_item_id zone_id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.getTrackableItemZoneAllowlistById as ITrackableItemZoneAllowlist;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const getPaginated = async (first = 20, offset = 0, query: Record<string, any> = {}) => {
        try { const q = `query($first:Int,$offset:Int,$query:JSON){ getPaginatedTrackableItemZoneAllowlists(first:$first,offset:$offset,query:$query){ id trackable_item_id zone_id } }`;
            const data = await graphqlRequest(authToken, q, { first, offset, query });
            return data.getPaginatedTrackableItemZoneAllowlists as ITrackableItemZoneAllowlist[];
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const create = async (input: ITrackableItemZoneAllowlistInput) => {
        try { const q = `mutation($input:TrackableItemZoneAllowlistInput!){ createTrackableItemZoneAllowlist(input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { input });
            return data.createTrackableItemZoneAllowlist as ITrackableItemZoneAllowlist;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const update = async (id: number, input: ITrackableItemZoneAllowlistInput) => {
        try { const q = `mutation($id:ID!,$input:TrackableItemZoneAllowlistInput!){ updateTrackableItemZoneAllowlist(id:$id,input:$input){ id } }`;
            const data = await graphqlRequest(authToken, q, { id, input });
            return data.updateTrackableItemZoneAllowlist as ITrackableItemZoneAllowlist;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    const remove = async (id: number) => {
        try { const q = `mutation($id:ID!){ deleteTrackableItemZoneAllowlist(id:$id){ id } }`;
            const data = await graphqlRequest(authToken, q, { id });
            return data.deleteTrackableItemZoneAllowlist as ITrackableItemZoneAllowlist;
        } catch (e:any) { errorCatcher(e, loginFunctions.logout); return null; }
    };

    return [items, { list, getById, getPaginated, create, update, remove }];
}


